import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { UNMOUNT_STATUS_WARNING, UNMOUNT_STATUS_DISALLOW, UNMOUNT_STATUS_ALLOW } from "components/Shared/MSection.tsx";

// This is used to create the state data structures needed by MSplitTabView, as well as provide an interface for
// invoking behavior on the view. It's much easier to just use WrappedMSplitTabView to avoid the extra steps.

const useMSplitTabView = ({
  tabs, // Same as MSectionCollection sections prop (each section also needs an id so we have something to reference it with)
  defaultRightTabIds, // List of ids to defualt on the right
  firstChildInitialSize, // Initial size of left pane when split
  getTabUnmountStatus, // Called with tab id, expected return is { canUnmountChildStatus, canUnmountChildStatus }
  onTabUnmount, // Called with tab ids that were unmounted
  onTabSplit, // Called when a tab is moved with the args tabIdMoved, newLeftTabs, newRightTabs
  initialTabs, // object to optionally display certain tabs on initial load
}) => {

  const tabIds = tabs.map(t => t.id);
  let defaultLeftTabIds = tabIds;
  if (defaultRightTabIds && defaultRightTabIds.length) {
    defaultLeftTabIds = defaultLeftTabIds.filter(id => defaultRightTabIds.indexOf(id) === -1);
  }

  const instance = useRef({
    unmountPushFunction: null,
    switchToTabId: null,
    oldTabIds: tabIds,
  });
  const [leftTabIds, setLeftTabIds] = useState(defaultLeftTabIds);
  const [rightTabIds, setRightTabIds] = useState(defaultRightTabIds || []);
  const [additionalTabs, setAdditionalTabs] = useState([]);
  const [unmountInfo, setUnmountInfo] = useState();
  const [leftControlMethods, setLeftControlMethods] = useState();
  const [rightControlMethods, setRightControlMethods] = useState();

  const allTabs = [...tabs, ...additionalTabs];

  // Going to compare the tabs being passed in to what was last passed in in case the user decided they want to pass us new tabs
  const newTabs = tabIds.filter(id => !instance.current.oldTabIds.find(i => i === id));
  const removedTabs = instance.current.oldTabIds.filter(id => !tabIds.find(i => i === id));

  if (removedTabs.length) {
    setLeftTabIds([...leftTabIds, ...newTabs].filter(id => !removedTabs.find(i => i === id)));
    setRightTabIds(rightTabIds.filter(id => !removedTabs.find(i => i === id)));
  } else if (newTabs.length) setLeftTabIds([...leftTabIds, ...newTabs]);
  instance.current.oldTabIds = tabIds;

  useEffect(() => {
    if (instance.current.switchToTabId) {
      switchToTab(instance.current.switchToTabId);
      instance.current.switchToTabId = null;
    }
  }, [leftTabIds, rightTabIds]);

  const getTabById = id => allTabs.find(t => t.id === id);

  const getLeftChild = () => {
    if (leftControlMethods && leftTabIds.length) {
      return leftControlMethods.getSelectedTab();
    } else {
      return null;
    }
  };

  const getRightChild = () => {
    if (rightControlMethods && rightTabIds.length) {
      return rightControlMethods.getSelectedTab();
    } else {
      return null;
    }
  };

  const getUnmountInfo = () => {
    if (!getTabUnmountStatus) return { canUnmountStatus: UNMOUNT_STATUS_ALLOW };
    let leftStatus = getTabUnmountStatus(getLeftChild().id);
    let rightChild = getRightChild();
    let rightStatus;
    if (rightChild) rightStatus = getTabUnmountStatus(rightChild.id);

    const getStrictestStatus = (...statuses) => {
      if (statuses.some(s => s === UNMOUNT_STATUS_DISALLOW)) return UNMOUNT_STATUS_DISALLOW;
      if (statuses.some(s => s === UNMOUNT_STATUS_WARNING)) return UNMOUNT_STATUS_WARNING;
      return UNMOUNT_STATUS_ALLOW;
    };

    let canUnmountStatus = getStrictestStatus(leftStatus && leftStatus.canUnmountChildStatus, rightStatus && rightStatus.canUnmountChildStatus);
    let canUnmountWarning;

    if (leftStatus && leftStatus.canUnmountChildStatus !== UNMOUNT_STATUS_ALLOW) {
      canUnmountWarning = leftStatus.canUnmountChildWarning;
    }

    if (rightStatus && rightStatus.canUnmountChildStatus !== UNMOUNT_STATUS_ALLOW) {
      if (canUnmountWarning) {
        canUnmountWarning = <>
          {canUnmountWarning}<br /><br />{rightStatus.canUnmountChildWarning}
        </>;
      } else {
        canUnmountWarning = rightStatus.canUnmountChildWarning;
      }
    }

    return { canUnmountStatus, canUnmountWarning };
  };

  const unmountYesAction = () => {
    instance.current.unmountPushFunction();
  };

  const unmountNoAction = () => {
    setUnmountInfo(null);
  };

  // Returns true if tab switch allowed
  // yea.... i know the name of this function sucks. if you have a better idea change it
  const updateStateIfUnmountUnallowed = () => {
    const { canUnmountStatus, canUnmountWarning } = getUnmountInfo();

    if (canUnmountStatus !== UNMOUNT_STATUS_ALLOW) {
      setUnmountInfo({ canUnmountStatus, canUnmountWarning });
      return false;
    }

    return true;
  };

  const pushTabLeft = (tabId, forcePush) => {
    instance.current.unmountPushFunction = () => pushTabLeft(tabId, true);
    let newTabs = pushTab(tabId, rightTabIds, leftTabIds, forcePush);

    if (newTabs) {
      setLeftTabIds(newTabs.to);
      setRightTabIds(newTabs.from);
      if (onTabSplit) onTabSplit(tabId, newTabs.to, newTabs.from);
    }
  };

  // only use this to set all tabs on each side, perhaps right after loading. This will skip important checks like if we can split a tab/unmount that tab
  const forceSetTabs = (leftTabIds, rightTabIds) => {
    setLeftTabIds(leftTabIds.filter(tid => tabs.some(t => t.id == tid)));
    setRightTabIds(rightTabIds.filter(tid => tabs.some(t => t.id == tid)));
  };

  const pushTabRight = (tabId, forcePush) => {
    instance.current.unmountPushFunction = () => pushTabRight(tabId, true);
    let newTabs = pushTab(tabId, leftTabIds, rightTabIds, forcePush);

    if (newTabs) {
      // If we pushed all tabs right, lets just put them all left and make that the display
      if (!newTabs.from.length) {
        newTabs.from = newTabs.to;
        newTabs.to = [];
      }

      setLeftTabIds(newTabs.from);
      setRightTabIds(newTabs.to);
      if (onTabSplit) onTabSplit(tabId, newTabs.from, newTabs.to);
    }
  };

  const pushTab = (tabId, from, to, forcePush) => {
    if (!forcePush && !updateStateIfUnmountUnallowed()) return null;

    const tab = from.find(id => id === tabId);

    if (tab) {
      let newFrom = from.filter(id => id !== tabId);
      let newTo = [...to, tab];
      instance.current.switchToTabId = tabId;

      if (onTabUnmount) {
        let leftChild = getLeftChild();
        let rightChild = getRightChild();
        if (rightChild) onTabUnmount(leftChild.id, rightChild.id);
        else onTabUnmount(leftChild.id);
      }
      setUnmountInfo(null);

      return { from: newFrom, to: newTo };
    }

    return null;
  };

  const addTabLeft = (tab) => addTab(tab, leftTabIds, setLeftTabIds);

  const addTabRight = (tab) => addTab(tab, rightTabIds, setRightTabIds);

  const switchToTab = id => {
    if (leftControlMethods) leftControlMethods.switchToTabId(id);
    if (rightControlMethods) rightControlMethods.switchToTabId(id);
  };

  const addTab = (tab, tabIds, setTabIdsFunc) => {
    if (getTabById(tab.id)) {
      // We added a tab that already exists, lets at least switch to it
      // switchToTabId will do nothing if the section collection doesnt find a tab with that id so this is fine
      switchToTab(tab.id);
      return tabIds;
    }

    instance.current.switchToTabId = tab.id;
    setAdditionalTabs([...additionalTabs, tab]);
    setTabIdsFunc([...tabIds, tab.id]);
  };

  const closeTab = (tabId, forceClose) => {
    let leftChild = getLeftChild();
    let rightChild = getRightChild();
    let tabIsActive = leftChild.id === tabId || (rightChild && rightChild.id === tabId);

    // Only have to worry about unmount status if the tab we are trying to close is mounted
    if (!forceClose && tabIsActive) {
      const unmountStatus = getTabUnmountStatus(tabId);

      if (unmountStatus && unmountStatus.canUnmountChildStatus !== UNMOUNT_STATUS_ALLOW) {
        instance.current.unmountPushFunction = () => closeTab(tabId, true);
        setUnmountInfo({ canUnmountStatus: unmountStatus.canUnmountChildStatus, canUnmountWarning: unmountStatus.canUnmountChildWarning });
        return;
      }
    }

    let newLeft = leftTabIds.filter(id => id !== tabId);
    let newRight = rightTabIds.filter(id => id !== tabId);

    if (!newLeft.length) {
      newLeft = newRight;
      newRight = [];
    }

    if (onTabUnmount && tabIsActive) onTabUnmount(tabId);

    ReactDOM.unstable_batchedUpdates(() => {
      setLeftTabIds(newLeft);
      setRightTabIds(newRight);
      setUnmountInfo(null);
      setAdditionalTabs(additionalTabs.filter(t => t.id !== tabId));
    });
  };

  // state, functions
  return [{
    tabs: allTabs,
    leftTabIds,
    rightTabIds,
    firstChildInitialSize,
    unmountInfo,
    initialTabs,
  },
  {
    pushTabLeft,
    pushTabRight,
    addTabLeft,
    addTabRight,
    switchToTab,
    closeTab,
    getLeftChild,
    getRightChild,
    setLeftControlMethods,
    setRightControlMethods,
    getUnmountInfo,
    getTabUnmountStatus,
    unmountYesAction,
    unmountNoAction,
    onTabUnmount,
    forceSetTabs,
  }];
};

export default useMSplitTabView;